import type { OrganizationSave } from "@/store/organization";
import type { UnitPreferenceSave } from "@/type/api";

/**
 * The Organization settings page has no Save button: each editable field queues
 * its own save on change and the shared indicator reports the outcome. The
 * field handlers live here rather than inline in the SFC so the wiring — a
 * change goes through the auto-save queue and carries only the field that
 * changed — is pinned by tests without mounting the component.
 */

/** The task queue from `useSaveState` (ts/lib/autoSave.ts). */
export type SaveRunner = (task: () => Promise<unknown>) => Promise<void>;

/** The slice of the organization store these handlers need. */
export interface OrganizationSaver {
	saveChanges(patch: OrganizationSave): Promise<void>;
}

export type OrganizationTextField =
	| "name"
	| "website"
	| "generalManagerName"
	| "officeAddressStreet"
	| "officeAddressCity"
	| "officeAddressPostalCode"
	| "officePhone"
	| "officeFax"
	| "officeEmail";

export type UnitOverrideField =
	| "area"
	| "mass"
	| "volume"
	| "areaBasis"
	| "volumeBasis";

/** A blank text field clears the stored value; anything else replaces it. */
export function textFieldPatch(
	field: OrganizationTextField,
	value: string,
): OrganizationSave {
	const patch: Partial<Record<OrganizationTextField, string | null>> = {};
	patch[field] = value === "" ? null : value;
	return patch;
}

/**
 * One unit override as the server wants it: the single dimension that changed.
 * An empty value ("Use preset default") clears it, so it inherits the preset.
 */
export function unitOverridePatch(
	field: UnitOverrideField,
	value: string,
): OrganizationSave {
	const unitPreference: Partial<UnitPreferenceSave> = {};
	unitPreference[field] = value;
	return { unitPreference };
}

export function unitPresetPatch(preset: string): OrganizationSave {
	return { unitPreference: { preset } };
}

export async function saveTextField(
	run: SaveRunner,
	org: OrganizationSaver,
	field: OrganizationTextField,
	event: Event,
): Promise<void> {
	const patch = textFieldPatch(field, (event.target as HTMLInputElement).value);
	await run(() => org.saveChanges(patch));
}

export async function saveUnitOverride(
	run: SaveRunner,
	org: OrganizationSaver,
	field: UnitOverrideField,
	event: Event,
): Promise<void> {
	const patch = unitOverridePatch(
		field,
		(event.target as HTMLSelectElement).value,
	);
	await run(() => org.saveChanges(patch));
}

export async function saveUnitPreset(
	run: SaveRunner,
	org: OrganizationSaver,
	event: Event,
): Promise<void> {
	const patch = unitPresetPatch((event.target as HTMLSelectElement).value);
	await run(() => org.saveChanges(patch));
}
